/**
 * London Smoke & Cure — Njord Monitor overview helpers (KPIs, status, thresholds).
 */

import { presenceTier } from './njordLiveService';
import { njordIsSetupMode } from './njordTwinUtils';
import { njordMonitoringIsPaused } from './njordMonitoringPauseUtils';
import {
    NJORD_DEFAULT_READ_INTERVAL_SEC,
    NJORD_PRESENCE_OFFLINE_INTERVALS,
    NJORD_PRESENCE_ONLINE_INTERVALS,
} from './njordConstants';

/**
 * @param {number|null|undefined} v
 */
function numOrNull(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/**
 * @param {object|null} latest
 * @param {object|null|undefined} thresholds
 * @returns {'none'|'warning'|'critical'}
 */
export function thresholdSeverity(latest, thresholds) {
    if (!latest || !thresholds || typeof thresholds !== 'object') return 'none';
    const tempC = numOrNull(latest.tempC);
    const humPct = numOrNull(latest.humPct);
    const minT = numOrNull(thresholds.minTemp);
    const maxT = numOrNull(thresholds.maxTemp);
    const minH = numOrNull(thresholds.minHum);
    const maxH = numOrNull(thresholds.maxHum);
    let critical = false;
    let warning = false;
    if (tempC != null && minT != null && tempC < minT) {
        if (tempC < minT - 1) critical = true;
        else warning = true;
    }
    if (tempC != null && maxT != null && tempC > maxT) {
        if (tempC > maxT + 1) critical = true;
        else warning = true;
    }
    if (humPct != null && minH != null && humPct < minH) warning = true;
    if (humPct != null && maxH != null && humPct > maxH) warning = true;
    if (critical) return 'critical';
    if (warning) return 'warning';
    return 'none';
}

/**
 * UI status for sensor cards (maps to odin-njord__pill--*).
 * While monitoring is paused, threshold tones are suppressed (PAUSED badge is separate) —
 * presence/setup still show so operators know the board is up.
 * @param {object|null} liveRow
 * @param {object|null|undefined} thresholds
 * @param {number} [readIntervalSec]
 * @param {{ monitoringPaused?: boolean }} [opts]
 * @returns {'online'|'degraded'|'offline'|'unknown'|'critical'|'caution'|'setup'}
 */
export function sensorDisplayStatus(liveRow, thresholds, readIntervalSec = 15, opts = {}) {
    if (njordIsSetupMode(liveRow)) return 'setup';
    const tier = presenceTierFromLive(liveRow, readIntervalSec);
    if (tier === 'offline' || tier === 'unknown') return tier === 'unknown' ? 'unknown' : 'offline';
    if (opts.monitoringPaused) {
        return tier === 'degraded' ? 'degraded' : 'online';
    }
    const sev = thresholdSeverity(liveRow?.latest, thresholds);
    if (sev === 'critical') return 'critical';
    if (tier === 'degraded') return 'degraded';
    if (sev === 'warning') return 'caution';
    return 'online';
}

/** Epoch seconds floor — matches firmware NJORD_NTP_EPOCH_MIN (~Nov 2023). */
const NJORD_EPOCH_SEC_MIN = 1_700_000_000;

/** Epoch-ms values only — firmware may send millis() before NTP sync. */
const NJORD_EPOCH_MS_MIN = 1_000_000_000_000;

/**
 * Best-effort timestamp (ms) for when the displayed reading was taken.
 * Rejects pre-NTP / millis()-as-epoch stamps (firmware may send those before wall clock).
 * @param {object|null|undefined} latest
 * @param {object|null|undefined} presence
 * @returns {number|null}
 */
export function readingTimestampMs(latest, presence) {
    const raw = numOrNull(latest?.ts ?? latest?.epoch);
    if (raw != null) {
        // Epoch seconds (normal twin path)
        if (raw >= NJORD_EPOCH_SEC_MIN && raw < NJORD_EPOCH_MS_MIN) return raw * 1000;
        // Already epoch ms
        if (raw >= NJORD_EPOCH_MS_MIN) return raw;
    }
    const sampleMs = numOrNull(latest?.sampleMs);
    if (sampleMs != null && sampleMs >= NJORD_EPOCH_MS_MIN) return sampleMs;
    const beat = numOrNull(presence?.lastBeatMs);
    return beat != null && beat >= NJORD_EPOCH_MS_MIN ? beat : null;
}

/**
 * Latest activity for presence — prefers fresh readings over a stale heartbeat.
 * @param {object|null|undefined} liveRow live/{id} object
 * @returns {number|null}
 */
export function njordEffectiveActivityMs(liveRow) {
    if (!liveRow || typeof liveRow !== 'object') return null;
    const readingMs = readingTimestampMs(liveRow.latest, liveRow.presence);
    const beat = numOrNull(liveRow.presence?.lastBeatMs ?? liveRow.lastBeatMs);
    const beatMs = beat != null && beat >= NJORD_EPOCH_MS_MIN ? beat : null;
    const candidates = [readingMs, beatMs].filter((v) => v != null);
    return candidates.length ? Math.max(...candidates) : null;
}

/**
 * Presence tier from full live row (readings + heartbeat).
 * @param {object|null|undefined} liveRow
 * @param {number} [readIntervalSec]
 */
export function presenceTierFromLive(liveRow, readIntervalSec = NJORD_DEFAULT_READ_INTERVAL_SEC) {
    return presenceTier(njordEffectiveActivityMs(liveRow), readIntervalSec);
}

/**
 * Human label for how long ago the reading values were captured.
 * @param {object|null|undefined} latest
 * @param {object|null|undefined} presence
 * @param {number} [readIntervalSec]
 */
export function formatReadingAge(latest, presence, readIntervalSec = NJORD_DEFAULT_READ_INTERVAL_SEC) {
    const ms = readingTimestampMs(latest, presence);
    if (ms == null) return 'No reading yet';
    const ageSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    const interval = Math.max(5, readIntervalSec);
    if (ageSec < 30) return 'Just now';
    if (ageSec < interval * 2) return `${ageSec}s ago`;
    if (ageSec < interval * NJORD_PRESENCE_ONLINE_INTERVALS) {
        return `${ageSec}s ago · on schedule`;
    }
    if (ageSec < interval * NJORD_PRESENCE_OFFLINE_INTERVALS) {
        return `${Math.floor(ageSec / 60) || 1}m ago · waiting`;
    }
    if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
    if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
    return `${Math.floor(ageSec / 86400)}d ago`;
}

/**
 * @param {number|null|undefined|object} lastBeatMsOrLiveRow epoch ms, or live/{id} row
 */
export function formatLastSeen(lastBeatMsOrLiveRow, readIntervalSec = NJORD_DEFAULT_READ_INTERVAL_SEC) {
    const lastBeatMs =
        lastBeatMsOrLiveRow != null && typeof lastBeatMsOrLiveRow === 'object'
            ? njordEffectiveActivityMs(lastBeatMsOrLiveRow)
            : lastBeatMsOrLiveRow;
    if (!lastBeatMs || !Number.isFinite(lastBeatMs)) return 'No check-in yet';
    const ageSec = Math.max(0, Math.floor((Date.now() - lastBeatMs) / 1000));
    const interval = Math.max(5, readIntervalSec);
    if (ageSec < 30) return 'Just now';
    if (ageSec < interval * 2) return `${ageSec}s ago`;
    if (ageSec < interval * NJORD_PRESENCE_ONLINE_INTERVALS) {
        return `${ageSec}s ago · on schedule`;
    }
    if (ageSec < interval * NJORD_PRESENCE_OFFLINE_INTERVALS) {
        return `${Math.floor(ageSec / 60) || 1}m ago · waiting for check-in`;
    }
    if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
    return `${Math.floor(ageSec / 3600)}h ago`;
}

/**
 * Plain-English note when status is not fully live but we still have readings.
 * @param {string} status
 * @param {number|null} lastBeatMs
 */
export function presenceStalenessNote(status, lastBeatMs, liveRow = null) {
    if (status === 'online' || status === 'setup') return '';
    const activityMs = liveRow ? njordEffectiveActivityMs(liveRow) : lastBeatMs;
    const ageSec =
        activityMs != null && Number.isFinite(activityMs)
            ? (Date.now() - activityMs) / 1000
            : null;
    const interval = NJORD_DEFAULT_READ_INTERVAL_SEC;
    if (ageSec != null && ageSec < interval * NJORD_PRESENCE_ONLINE_INTERVALS) {
        return '';
    }
    if (!lastBeatMs && !liveRow) return '';
    if (status === 'degraded' || status === 'caution') {
        return 'Check-in is a little late — numbers below are still the last reading from the sensor.';
    }
    if (status === 'offline' || status === 'unknown') {
        return 'No recent check-in — numbers below are the last known reading, not live.';
    }
    return '';
}

/**
 * @param {object} room
 * @param {string} sensorId
 */
export function sensorRoomLabel(rooms, sensorId) {
    for (const room of rooms || []) {
        const members = room.members && typeof room.members === 'object' ? Object.keys(room.members) : [];
        if (members.includes(sensorId)) return room.label || room.id || '';
    }
    return '';
}

/**
 * @param {object[]} rooms
 * @param {string} sensorId
 */
export function sensorThresholds(rooms, sensorId) {
    for (const room of rooms || []) {
        const members = room.members && typeof room.members === 'object' ? Object.keys(room.members) : [];
        if (members.includes(sensorId) && room.thresholds) return room.thresholds;
    }
    return null;
}

/**
 * Active (non-archived) registry rows keyed by sensor id.
 * @param {object[]} registry
 * @returns {Map<string, object>}
 */
export function njordActiveRegistryMap(registry = []) {
    const map = new Map();
    for (const r of registry) {
        if (r?.archived) continue;
        const id = r.id || r.sensorId;
        if (id) map.set(id, r);
    }
    return map;
}

/**
 * Sensor has checked in at least once (presence or stored reading on live twin).
 * @param {object|null|undefined} liveRow
 */
export function njordSensorHasLiveConnection(liveRow) {
    if (!liveRow || typeof liveRow !== 'object') return false;
    if (njordEffectiveActivityMs(liveRow) != null) return true;
    const latest = liveRow.latest;
    if (!latest || typeof latest !== 'object') return false;
    return (
        numOrNull(latest.ts) != null ||
        numOrNull(latest.tempC) != null ||
        numOrNull(latest.humPct) != null ||
        numOrNull(latest.weightKg) != null ||
        numOrNull(latest.weight_kg) != null
    );
}

/**
 * @param {object[]} registry
 * @param {Record<string, object>} live
 * @param {object[]} rooms
 * @param {{ connectedOnly?: boolean }} [opts] — overview uses connectedOnly: active registry + live check-in
 */
export function buildMonitorSensorList(registry, live, rooms, opts = {}) {
    const { connectedOnly = false } = opts;
    const active = njordActiveRegistryMap(registry);
    const ids = [...active.keys()].filter((id) => {
        if (!connectedOnly) return true;
        return njordSensorHasLiveConnection(live?.[id]);
    });

    return ids.map((id) => {
        const reg = active.get(id) || {};
        const liveRow = live?.[id] || null;
        const thresholds = sensorThresholds(rooms, id);
        const readInterval = numOrNull(liveRow?.reported?.readIntervalSec) || 15;
        const monitoringPaused = njordMonitoringIsPaused(reg.monitoring);
        const deviceClass = String(reg.deviceClass || reg.device_class || '').trim().toLowerCase();
        return {
            id,
            label: reg.label || id,
            roomLabel: sensorRoomLabel(rooms, id),
            live: liveRow,
            thresholds,
            readIntervalSec: readInterval,
            deviceClass: deviceClass || '',
            monitoringPaused,
            pauseNote: reg.monitoring?.pauseNote ? String(reg.monitoring.pauseNote) : '',
            status: sensorDisplayStatus(liveRow, thresholds, readInterval, { monitoringPaused }),
        };
    });
}

/**
 * @param {ReturnType<typeof buildMonitorSensorList>} sensors
 * @param {object[]} rooms
 */
export function computeFacilityStats(sensors, rooms) {
    const total = sensors.length;
    let online = 0;
    let degraded = 0;
    let offline = 0;
    let openAlerts = 0;
    let criticalCount = 0;
    let warningCount = 0;

    for (const s of sensors) {
        if (s.status === 'online') online += 1;
        else if (s.status === 'degraded' || s.status === 'caution') degraded += 1;
        else offline += 1;

        if (s.status === 'critical') {
            openAlerts += 1;
            criticalCount += 1;
        } else if (s.status === 'caution' || s.status === 'degraded') {
            openAlerts += 1;
            warningCount += 1;
        }
    }

    const zoneCount = (rooms || []).length;

    return {
        total,
        online,
        degraded,
        offline,
        openAlerts,
        criticalCount,
        warningCount,
        roomCount: zoneCount,
    };
}

/**
 * @param {ReturnType<typeof buildMonitorSensorList>} sensors
 */
export function buildAttentionList(sensors) {
    return sensors
        .filter((s) => ['offline', 'unknown', 'critical', 'degraded', 'caution'].includes(s.status))
        .sort((a, b) => {
            const rank = { critical: 0, offline: 1, unknown: 2, degraded: 3, caution: 4, online: 5 };
            return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
        })
        .slice(0, 8);
}

/**
 * @param {ReturnType<typeof buildMonitorSensorList>} sensors
 */
export function buildAlertFeed(sensors) {
    return sensors
        .filter((s) => s.status === 'critical' || s.status === 'caution')
        .map((s) => {
            const latest = s.live?.latest || {};
            const sev = s.status === 'critical' ? 'critical' : 'warning';
            const t = numOrNull(latest.tempC);
            const h = numOrNull(latest.humPct);
            const th = s.thresholds || {};
            let message = 'Threshold attention';
            let metric = 'temperature';
            if (t != null && numOrNull(th.maxTemp) != null && t > th.maxTemp) {
                message = 'Temperature above high threshold';
                metric = 'temperature';
            } else if (t != null && numOrNull(th.minTemp) != null && t < th.minTemp) {
                message = 'Temperature below low threshold';
                metric = 'temperature';
            } else if (h != null && numOrNull(th.maxHum) != null && h > th.maxHum) {
                message = 'Humidity above high threshold';
                metric = 'humidity';
            } else if (h != null && numOrNull(th.minHum) != null && h < th.minHum) {
                message = 'Humidity below low threshold';
                metric = 'humidity';
            } else if (s.status === 'degraded') {
                message = 'Heartbeat delayed';
                metric = 'presence';
            }
            return {
                id: s.id,
                sensorId: s.id,
                sensorName: s.label,
                zone: s.roomLabel || 'Unassigned',
                severity: sev,
                message,
                metric,
                value: t != null ? `${t.toFixed(1)} °C` : h != null ? `${h.toFixed(0)} %RH` : '—',
                threshold:
                    metric === 'humidity' && h != null && th.maxHum != null
                        ? `max ${th.maxHum} %RH`
                        : metric === 'humidity' && h != null && th.minHum != null
                          ? `min ${th.minHum} %RH`
                          : t != null && th.maxTemp != null
                            ? `max ${th.maxTemp} °C`
                            : t != null && th.minTemp != null
                              ? `min ${th.minTemp} °C`
                              : '',
                triggeredAt: formatLastSeen(s.live),
            };
        })
        .slice(0, 6);
}
